import React, { useEffect, useState } from 'react';
import { AiOutlineLoading } from 'react-icons/ai';
import Styles from '../assets/css/productDetail.module.scss';
import Loader from '../utils/Loader';

const QuantityManager = ({
  productData,
  productTransactionDataWithQuantity,
  productLoading,
  submitLoading,
  handleIncrease,
  handleDecrease,
  handleAddMore,
  handleFinish,
  originalQuantities,
  isOutbound,
  isNewlyCreated,
  setProductTransactionDataWithQuantity,
  setProductsData,
  showPopup,
  selectedSku,
  skuQuantities,
  onSkuQuantityChange,
  getCurrentSkuQuantity,
  getAvailableQuantityForSku
}) => {
  const [displayQuantity, setDisplayQuantity] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [manualQuantity, setManualQuantity] = useState('');

  // Get current quantity for selected SKU
  const getCurrentQuantity = () => {
    if (!selectedSku) return 0;
    return getCurrentSkuQuantity();
  };

  // Get available quantity for selected SKU
  const getAvailableQuantity = () => {
    if (!selectedSku) return 0;
    return getAvailableQuantityForSku();
  };

  // Update display quantity when SKU changes or quantities update
  useEffect(() => {
    const newQuantity = getCurrentQuantity();
    setDisplayQuantity(newQuantity);
    setManualQuantity(newQuantity.toString());
  }, [selectedSku, skuQuantities, getCurrentSkuQuantity]);

  const isDecreaseDisabled = () => {
    if (productLoading || submitLoading || !selectedSku) return true;

    const currentQty = getCurrentQuantity();

    if (isOutbound) {
      return currentQty <= 1;
    } else {
      const availableQty = getAvailableQuantity();
      return currentQty <= availableQty;
    }
  };

  const isIncreaseDisabled = () => {
    if (productLoading || submitLoading || !selectedSku) return true;

    if (isOutbound) {
      const currentQty = getCurrentQuantity();
      const availableQty = getAvailableQuantity();
      return currentQty >= availableQty;
    }

    return false;
  };

  const handleIncreaseClick = () => {
    if (isOutbound) {
      const currentQty = getCurrentQuantity();
      const availableQty = getAvailableQuantity();

      if (currentQty >= availableQty) {
        showPopup('error', 'Maximum Quantity Reached', `You have reached the maximum available quantity of ${availableQty} units for this SKU.`);
        return;
      }
    }

    handleIncrease(productData?.id);
  };

  const handleManualQuantityChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setManualQuantity(value);

      if (isOutbound && value) {
        const newQuantity = parseInt(value) || 0;
        const availableQty = getAvailableQuantity();
        if (newQuantity > availableQty) {
          showPopup('error', 'Maximum Quantity Reached', `Cannot outbound more than available quantity (${availableQty}).`);
        }
      }
    }
  };

  const handleManualQuantitySubmit = () => {
    if (!productData || !selectedSku) return;

    const newQuantity = parseInt(manualQuantity) || 0;

    if (newQuantity < 0) {
      showPopup('error', 'Invalid Quantity', 'Quantity cannot be negative');
      setManualQuantity(displayQuantity.toString());
      setIsEditing(false);
      return;
    }

    if (isOutbound) {
      const availableQty = getAvailableQuantity();
      if (newQuantity > availableQty) {
        showPopup('error', 'Maximum Quantity Reached', `Cannot outbound more than available quantity (${availableQty}).`);
        setManualQuantity(displayQuantity.toString());
        setIsEditing(false);
        return;
      }
      if (newQuantity < 1) {
        showPopup('error', 'Quantity Limit', 'Outbound quantity must be at least 1.');
        setManualQuantity(displayQuantity.toString());
        setIsEditing(false);
        return;
      }
    } else {
      const availableQty = getAvailableQuantity();
      if (newQuantity < availableQty) {
        showPopup('error', 'Quantity Limit', `Inbound quantity cannot be less than available quantity (${availableQty}).`);
        setManualQuantity(displayQuantity.toString());
        setIsEditing(false);
        return;
      }
    }

    // Update SKU quantity
    onSkuQuantityChange(selectedSku.SkuId, newQuantity);
    setIsEditing(false);
  };

  const handleQuantityClick = () => {
    if (!productLoading && !submitLoading && selectedSku) {
      setIsEditing(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleManualQuantitySubmit();
    } else if (e.key === 'Escape') {
      setManualQuantity(displayQuantity.toString());
      setIsEditing(false);
    }
  };

  return (
    <div className={`${Styles.manage_product}`}>
      {productLoading ? (
        <div className={`${Styles.quantity_loader}`}>
          <Loader size="large" color="black" />
        </div>
      ) : (
        <>
          <h1>SKU #  <span>{selectedSku?.SellerSku || productData?.sku_number || ' N/A'}</span></h1>

          {/* Show variant info for multiple SKUs */}
          {productData?.lazada_product?.data?.skus?.length > 1 && selectedSku && (
            <div className={`${Styles.variant_info}`}>
              <p><strong>Variant:</strong> {Object.values(selectedSku.variation).join(' - ')}</p>
              <p><strong>Available:</strong> {getAvailableQuantity()} units</p>
              <p><strong>Currently Selected:</strong> {getCurrentQuantity()} units</p>
            </div>
          )}

          <div className={`${Styles.counter_Container}`}>
            <button
              className={`${Styles.button}`}
              onClick={() => handleDecrease(productData?.id)}
              disabled={isDecreaseDisabled()}
            >
              -
            </button>

            {isEditing ? (
              <input
                type="text"
                className={`${Styles.quantity_input}`}
                value={manualQuantity}
                onChange={handleManualQuantityChange}
                onBlur={handleManualQuantitySubmit}
                onKeyDown={handleKeyPress}
                autoFocus
              />
            ) : (
              <span
                className={`${Styles.count} ${!productLoading && !submitLoading ? Styles.editable : ''}`}
                onClick={handleQuantityClick}
              >
                {displayQuantity}
              </span>
            )}

            <button
              className={`${Styles.button}`}
              onClick={handleIncreaseClick}
              disabled={isIncreaseDisabled()}
            >
              +
            </button>
          </div>

          <div className={`${Styles.product_buttons}`}>
            <button
              onClick={handleAddMore}
              disabled={submitLoading}
            >
              + Add and Scan more
            </button>

            <button
              onClick={handleFinish}
              disabled={submitLoading || productLoading}
            >
              {submitLoading ? <AiOutlineLoading /> : "Finish"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuantityManager;